import { useCallback, useEffect, useRef } from 'react';

import {
  DevAssetAdmin,
  DevPlanContent,
} from 'state';

/**
 * Debounced patch buffer for optimistic per-entity autosave. Free-text fields call
 * `push({field: value})`; edits within `delay` coalesce into one PATCH. Discrete
 * controls (selects/switches) should bypass this and commit immediately. Any pending
 * edit is flushed on unmount (i.e. when the selection changes and the inspector
 * remounts), so a half-typed field is never lost.
 */
export function usePatchBuffer<T extends object>(
  commit: (patch: Partial<T>) => void,
  delay = 500
): { push: (patch: Partial<T>) => void; flush: () => void } {
  const pending = useRef<Partial<T>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (Object.keys(pending.current).length) {
      const patch = pending.current;
      pending.current = {};
      commitRef.current(patch);
    }
  }, []);

  const push = useCallback(
    (patch: Partial<T>) => {
      pending.current = { ...pending.current, ...patch };
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delay);
    },
    [flush, delay]
  );

  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []); // flush on unmount

  return { push, flush };
}

export interface Readiness {
  ready: boolean;
  problems: string[];
  issueNodeCodes: Set<string>;
}

/**
 * Client-side pre-check that mirrors the server's N5 publish-completeness gate. The
 * server remains authoritative (publish 409s with the real list); this just lets the
 * editor surface blockers live so publish is never a surprise, and flags the offending
 * nodes on the canvas.
 */
export function computeReadiness(
  content: DevPlanContent | undefined,
  assets: DevAssetAdmin[]
): Readiness {
  const problems: string[] = [];
  const issueNodeCodes = new Set<string>();
  if (!content) return { ready: true, problems, issueNodeCodes };

  const assetById = new Map(assets.map((a) => [a.id, a]));
  const nodeById = new Map(content.nodes.map((n) => [n.id, n]));
  const activeNodes = content.nodes.filter((n) => n.is_active);

  // 1. Active non-mastery node without playable content. Playable is EITHER a
  //    built scenario asset (HTML nodes) OR a non-empty blueprint list
  //    (assessment-backed nodes, e.g. Idea Generator) — mirrors the server gate.
  activeNodes.forEach((n) => {
    if (n.tier === 'mastery') return;
    if (n.detail?.blueprints?.length) return;
    const asset = n.scenario_asset != null ? assetById.get(n.scenario_asset) : undefined;
    if (!asset || !asset.is_built) {
      problems.push(`Node "${n.code}" has no built scenario asset.`);
      issueNodeCodes.add(n.code);
    }
  });

  // 2. Track with no active nodes.
  content.tracks.forEach((t) => {
    if (!activeNodes.some((n) => n.track === t.id)) {
      problems.push(`Track "${t.name}" has no active nodes.`);
    }
  });

  // 3. Edge touching an inactive / missing node.
  content.edges.forEach((e) => {
    const from = nodeById.get(e.from_node);
    const to = nodeById.get(e.to_node);
    if (!from || !to || !from.is_active || !to.is_active) {
      const label = to?.code || from?.code || 'unknown';
      problems.push(`A link on node "${label}" points at an inactive node.`);
      if (from?.is_active) issueNodeCodes.add(from.code);
      if (to?.is_active) issueNodeCodes.add(to.code);
    }
  });

  // 4. Remediation route with no (valid) target.
  content.remediation.forEach((r) => {
    const node = nodeById.get(r.node);
    if (!node || !node.is_active) return;
    const toNode = r.target_node != null ? nodeById.get(r.target_node) : undefined;
    const hasGame = r.target_game != null;
    const hasNode = !!toNode && toNode.is_active;
    if (!hasGame && !hasNode) {
      problems.push(
        `Remediation "${r.gate_name || r.gate_key}" on "${node.code}" has no valid target.`
      );
      issueNodeCodes.add(node.code);
    }
  });

  return { ready: problems.length === 0, problems, issueNodeCodes };
}
